import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { GoalInput } from '@/client/api/hr/perf';
import { hasLeft, GOAL_STATUS_LABEL, type Employee, type Goal, type GoalStatus, type ReviewCycle } from '@/client/types/hr';

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

/** Add or edit a goal. With `employees` it asks whose goal it is (HR);
 *  without, it is the caller's own (My workplace). */
export function GoalDialog({
  open,
  goal,
  cycles,
  employees,
  busy,
  onClose,
  onSave,
}: {
  open: boolean;
  goal: Goal | null;
  cycles: ReviewCycle[];
  employees?: Employee[];
  busy: boolean;
  onClose: () => void;
  onSave: (body: GoalInput) => void;
}) {
  const [form, setForm] = useState({ employeeId: '', title: '', description: '', cycleId: '', dueDate: '', status: 'not_started' as GoalStatus, progress: 0 });

  useEffect(() => {
    if (!open) return;
    setForm({
      employeeId: goal?.employeeId ?? '',
      title: goal?.title ?? '',
      description: goal?.description ?? '',
      cycleId: goal?.cycleId ?? cycles.find((c) => c.status === 'active')?.id ?? '',
      dueDate: goal?.dueDate ?? '',
      status: goal?.status ?? 'not_started',
      progress: goal?.progress ?? 0,
    });
  }, [open, goal, cycles]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      id: goal?.id,
      ...(employees && !goal ? { employeeId: form.employeeId } : {}),
      title: form.title.trim(),
      description: form.description.trim(),
      cycleId: form.cycleId || null,
      dueDate: form.dueDate || null,
      status: form.status,
      progress: form.progress,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{goal ? 'Edit goal' : 'Add goal'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {employees && !goal && (
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">Whose goal</span>
              <select required value={form.employeeId} onChange={(e) => setForm((f) => ({ ...f, employeeId: e.target.value }))} className={inputClass}>
                <option value="">Select someone</option>
                {employees
                  .filter((e) => !hasLeft(e))
                  .map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                    </option>
                  ))}
              </select>
            </label>
          )}
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-gray-600">Goal</span>
            <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className={inputClass} />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-gray-600">How you will know it is done (optional)</span>
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2"
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">Review cycle</span>
              <select value={form.cycleId} onChange={(e) => setForm((f) => ({ ...f, cycleId: e.target.value }))} className={inputClass}>
                <option value="">None</option>
                {cycles
                  .filter((c) => c.status !== 'closed' || c.id === form.cycleId)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </label>
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">Due</span>
              <input type="date" value={form.dueDate} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} className={inputClass} />
            </label>
          </div>
          {goal && (
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Status</span>
                <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as GoalStatus }))} className={inputClass}>
                  {Object.entries(GOAL_STATUS_LABEL).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Progress: {form.progress}%</span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={form.progress}
                  onChange={(e) => setForm((f) => ({ ...f, progress: Number(e.target.value) }))}
                  className="w-full"
                />
              </label>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
